import isArray from "lodash/isArray";
import isPlainObject from "lodash/isPlainObject";
import type { BaseBlock, BaseProp } from "../DataModel.types";

export type ChildrenProp = BaseProp & {
  prop: "children";
  type: "children";
  value: BaseBlock[];
};

export const isObj = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === "object" && isPlainObject(v);

export const isChildrenProp = (prop: unknown): prop is ChildrenProp => {
  if (!isObj(prop)) return false;
  const maybe = prop as Partial<ChildrenProp> & {
    prop?: unknown;
    type?: unknown;
    value?: unknown;
  };
  const hasCorrectType = maybe.type === "children";
  const hasCorrectProp = maybe.prop === undefined || maybe.prop === "children";
  return hasCorrectType && hasCorrectProp && isArray(maybe.value);
};

export const findAllChildrenWithNames = (
  blockProps: unknown,
  visited = new WeakSet<Record<string, unknown>>(),
): { propName: string; children: BaseBlock[] }[] => {
  const allChildrenGroups: { propName: string; children: BaseBlock[] }[] = [];
  if (!isObj(blockProps)) return allChildrenGroups;

  const queue: Record<string, unknown>[] = [blockProps];

  while (queue.length) {
    const current = queue.shift();
    if (!current) continue;
    if (visited.has(current)) continue;

    visited.add(current);

    for (const [key, prop] of Object.entries(current)) {
      if (isChildrenProp(prop)) {
        allChildrenGroups.push({
          propName: key,
          children: prop.value,
        });
        continue;
      }
      if (isObj(prop)) {
        queue.push(prop);
      }
    }
  }

  return allChildrenGroups;
};

export const extractChildBlocks = (
  props: Record<string, unknown>,
): BaseBlock[] => {
  const childBlocks: BaseBlock[] = [];
  for (const prop of Object.values(props)) {
    if (isChildrenProp(prop)) {
      childBlocks.push(...prop.value);
    }
  }
  return childBlocks;
};
